import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Plus, Trash2, Loader2, RotateCcw } from 'lucide-react';
import { api } from '../../lib/api';
import type { AiSettingsDefaults } from '../../lib/api';
import type { AiSettings } from '@palacete/shared/ai-settings';

const inputCls =
  'w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text placeholder:text-ui-text-subtle focus:outline-none focus:border-ui-accent';
const areaCls = `${inputCls} resize-y min-h-[5rem] font-mono`;

interface HintRow { industry: string; hint: string }

function Field({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm text-ui-text-muted mb-1">{label}</label>
      {children}
      {help && <p className="text-sm text-ui-text-subtle mt-1">{help}</p>}
    </div>
  );
}

// Campo de prompt: muestra el texto efectivo (editable en sitio) y, cuando difiere
// del que trae el sistema, ofrece restaurarlo de un clic.
function PromptField({ label, value, defaultValue, onChange }: {
  label: string; value: string; defaultValue: string; onChange: (v: string) => void;
}) {
  const changed = value.trim() !== defaultValue.trim();
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm text-ui-text-muted">{label}</label>
        {changed && (
          <button type="button" onClick={() => onChange(defaultValue)}
            className="flex items-center gap-1 text-sm text-ui-text-subtle hover:text-ui-accent">
            <RotateCcw size={13} /> Restaurar
          </button>
        )}
      </div>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} className={areaCls} />
    </div>
  );
}

// Editor de pistas por industria (filas industria → texto). Reutilizable para el
// estilo de imagen y para el tono de texto. `defaults` es el catálogo del sistema:
// los giros conocidos se muestran pre-rellenados y, si los cambias, puedes
// restaurarlos. Solo se guarda lo que difiera del default (ver buildPayload).
function HintEditor({ rows, onChange, defaults, industryPlaceholder, hintPlaceholder }: {
  rows: HintRow[];
  onChange: (rows: HintRow[]) => void;
  defaults: Record<string, string>;
  industryPlaceholder: string;
  hintPlaceholder: string;
}) {
  const update = (i: number, patch: Partial<HintRow>) =>
    onChange(rows.map((r, j) => j === i ? { ...r, ...patch } : r));
  return (
    <div className="space-y-2">
      <div className="hidden items-center gap-2 px-1 sm:grid sm:grid-cols-[10rem,minmax(0,1fr),auto]">
        <p className="text-xs font-medium uppercase tracking-[0.12em] text-ui-text-subtle">Industria</p>
        <p className="text-xs font-medium uppercase tracking-[0.12em] text-ui-text-subtle">Estilo</p>
        <span aria-hidden="true" />
      </div>
      {rows.map((row, i) => {
        const def = defaults[row.industry.trim()];
        const changed = def !== undefined && row.hint.trim() !== def.trim();
        return (
          <div key={i} className="rounded-lg border border-ui-border bg-ui-surface/35 p-2 sm:grid sm:grid-cols-[10rem,minmax(0,1fr),auto] sm:items-start sm:gap-2">
            <div className="sm:hidden mb-2">
              <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-ui-text-subtle">Industria</p>
            </div>
            <input value={row.industry} placeholder={industryPlaceholder}
              onChange={(e) => update(i, { industry: e.target.value })}
              className={`${inputCls} min-w-0`} />
            <div className="mt-2 sm:mt-0">
              <div className="sm:hidden mb-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-ui-text-subtle">Estilo</p>
              </div>
              <input value={row.hint} placeholder={hintPlaceholder}
                onChange={(e) => update(i, { hint: e.target.value })} className={inputCls} />
              {changed && (
                <button type="button" onClick={() => update(i, { hint: def })}
                  className="flex items-center gap-1 text-sm text-ui-text-subtle hover:text-ui-accent mt-1">
                  <RotateCcw size={12} /> Restaurar
                </button>
              )}
            </div>
            <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))}
              className="mt-2 inline-flex h-10 w-10 items-center justify-center rounded-md border border-ui-border text-ui-text-subtle transition-colors hover:border-ui-danger/40 hover:bg-ui-danger/10 hover:text-ui-danger sm:mt-0" aria-label="Quitar">
              <Trash2 size={16} />
            </button>
          </div>
        );
      })}
      <button type="button" onClick={() => onChange([...rows, { industry: '', hint: '' }])}
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-ui-border px-3 py-2 text-sm text-ui-text-muted transition-colors hover:border-ui-border-strong hover:text-ui-text">
        <Plus size={14} /> Añadir industria
      </button>
    </div>
  );
}

// Construye las filas iniciales: cada giro del catálogo con su valor efectivo
// (override guardado o, si no, el default del sistema), más cualquier override
// de un giro que no esté en el catálogo.
function mergeHintRows(defaults: Record<string, string>, overrides: Record<string, string>): HintRow[] {
  const rows: HintRow[] = Object.keys(defaults).map((industry) => ({
    industry, hint: overrides[industry] ?? defaults[industry],
  }));
  for (const [industry, hint] of Object.entries(overrides)) {
    if (!(industry in defaults)) rows.push({ industry, hint });
  }
  return rows;
}

export default function AiSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [defaults, setDefaults] = useState<AiSettingsDefaults | null>(null);
  const [hasApiKey, setHasApiKey] = useState(true);
  const [showPrompts, setShowPrompts] = useState(false);

  // Texto plano del formulario (vacío = usar el valor por defecto).
  const [imageModel, setImageModel] = useState('');
  const [textModel, setTextModel] = useState('');
  const [imageEnabled, setImageEnabled] = useState(false);
  const [imageMonthlyLimit, setImageMonthlyLimit] = useState('');
  const [textDailyLimit, setTextDailyLimit] = useState('');
  const [imageDefaultSize, setImageDefaultSize] = useState('');
  const [imageDefaultQuality, setImageDefaultQuality] = useState('');
  const [imageDefaultBackground, setImageDefaultBackground] = useState('');
  const [pImageDirective, setPImageDirective] = useState('');
  const [pCopywriter, setPCopywriter] = useState('');
  const [pBranding, setPBranding] = useState('');
  const [pAltText, setPAltText] = useState('');
  const [pTranslator, setPTranslator] = useState('');
  const [hints, setHints] = useState<HintRow[]>([]);
  const [copyHints, setCopyHints] = useState<HintRow[]>([]);

  useEffect(() => {
    api.admin.getAiSettings().then(({ settings, defaults, hasApiKey }) => {
      setDefaults(defaults);
      setHasApiKey(hasApiKey);
      setImageModel(settings.imageModel ?? '');
      setTextModel(settings.textModel ?? '');
      setImageEnabled(settings.imageEnabled ?? defaults.imageEnabled);
      setImageMonthlyLimit(settings.imageMonthlyLimit?.toString() ?? '');
      setTextDailyLimit(settings.textDailyLimit?.toString() ?? '');
      setImageDefaultSize(settings.imageDefaultSize ?? '');
      setImageDefaultQuality(settings.imageDefaultQuality ?? '');
      setImageDefaultBackground(settings.imageDefaultBackground ?? '');
      // Los prompts se muestran SIEMPRE con su texto efectivo (el guardado o, si no
      // se ha personalizado, el que trae el sistema) para que el SaaS owner los vea
      // y los edite en sitio, no como un campo vacío.
      setPImageDirective(settings.prompts?.imageDirective ?? defaults.prompts.imageDirective);
      setPCopywriter(settings.prompts?.copywriterSystem ?? defaults.prompts.copywriterSystem);
      setPBranding(settings.prompts?.brandingDirectorSystem ?? defaults.prompts.brandingDirectorSystem);
      setPAltText(settings.prompts?.altTextSystem ?? defaults.prompts.altTextSystem);
      setPTranslator(settings.prompts?.translatorSystem ?? defaults.prompts.translatorSystem);
      setHints(mergeHintRows(defaults.industryStyleHints, settings.industryStyleHints ?? {}));
      setCopyHints(mergeHintRows(defaults.industryCopyHints, settings.industryCopyHints ?? {}));
    }).catch((e) => setError((e as Error).message)).finally(() => setLoading(false));
  }, []);

  function buildPayload(): AiSettings {
    const t = (s: string) => (s.trim() ? s.trim() : undefined);
    const numOr = (s: string) => { const n = Number(s); return Number.isFinite(n) && n > 0 ? n : undefined; };
    // Solo se persiste lo que difiera del catálogo del sistema: así el default
    // sigue viviendo en código (y se actualiza solo si lo cambiamos ahí).
    const toRecord = (rows: HintRow[], catalog: Record<string, string>) => {
      const rec: Record<string, string> = {};
      for (const r of rows) {
        const ind = r.industry.trim(); const hint = r.hint.trim();
        if (!ind || !hint) continue;
        if (catalog[ind] !== undefined && catalog[ind].trim() === hint) continue;
        rec[ind] = hint;
      }
      return Object.keys(rec).length ? rec : undefined;
    };
    return {
      imageModel: t(imageModel),
      textModel: t(textModel),
      imageEnabled,
      imageMonthlyLimit: numOr(imageMonthlyLimit),
      textDailyLimit: numOr(textDailyLimit),
      imageDefaultSize: t(imageDefaultSize),
      imageDefaultQuality: t(imageDefaultQuality),
      imageDefaultBackground: t(imageDefaultBackground),
      prompts: {
        imageDirective: t(pImageDirective),
        copywriterSystem: t(pCopywriter),
        brandingDirectorSystem: t(pBranding),
        altTextSystem: t(pAltText),
        translatorSystem: t(pTranslator),
      },
      industryStyleHints: toRecord(hints, defaults?.industryStyleHints ?? {}),
      industryCopyHints: toRecord(copyHints, defaults?.industryCopyHints ?? {}),
    };
  }

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await api.admin.saveAiSettings(buildPayload());
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-ui-text-subtle py-6">
        <Loader2 size={16} className="animate-spin" /> Cargando configuración…
      </div>
    );
  }

  return (
    <div className="space-y-5" onChange={() => setSaved(false)}>
      {!hasApiKey && (
        <div className="rounded-md bg-ui-surface-2 px-3 py-2.5 text-sm text-ui-text-muted">
          Falta la clave de acceso a OpenRouter. Mientras no esté configurada (como dato secreto del sistema),
          la creación con IA no funcionará aunque la dejes encendida aquí.
        </div>
      )}

      {/* ── Generación de imágenes ─────────────────────────────────────────── */}
      <section className="space-y-4">
        <h3 className="text-sm font-medium text-ui-text">Generación de imágenes</h3>

        <label className="flex items-center gap-2.5 cursor-pointer">
          <input type="checkbox" checked={imageEnabled} onChange={(e) => setImageEnabled(e.target.checked)}
            className="h-4 w-4 accent-ui-accent" />
          <span className="text-sm text-ui-text">Permitir crear imágenes con IA en todos los sitios</span>
        </label>

        <Field label="Modelo de imagen" help={defaults?.imageModel ? `Si lo dejas vacío se usa: ${defaults.imageModel}` : 'Identificador del modelo en OpenRouter.'}>
          <input value={imageModel} onChange={(e) => setImageModel(e.target.value)}
            placeholder={defaults?.imageModel || 'sourceful/riverflow-v2.5-fast'} className={`${inputCls} font-mono`} />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Límite de imágenes por negocio al mes" help={`Por defecto: ${defaults?.imageMonthlyLimit ?? 30}`}>
            <input type="number" min={1} value={imageMonthlyLimit} onChange={(e) => setImageMonthlyLimit(e.target.value)}
              placeholder={String(defaults?.imageMonthlyLimit ?? 30)} className={inputCls} />
          </Field>
          <Field label="Tamaño por defecto" help={defaults?.imageDefaultSize ? `Por defecto: ${defaults.imageDefaultSize}` : 'Ej. 1536x1024'}>
            <input value={imageDefaultSize} onChange={(e) => setImageDefaultSize(e.target.value)}
              placeholder={defaults?.imageDefaultSize || '1536x1024'} className={`${inputCls} font-mono`} />
          </Field>
          <Field label="Calidad por defecto" help={defaults?.imageDefaultQuality ? `Por defecto: ${defaults.imageDefaultQuality}` : undefined}>
            <input value={imageDefaultQuality} onChange={(e) => setImageDefaultQuality(e.target.value)}
              placeholder={defaults?.imageDefaultQuality || 'medium'} className={inputCls} />
          </Field>
          <Field label="Fondo por defecto" help={defaults?.imageDefaultBackground ? `Por defecto: ${defaults.imageDefaultBackground}` : undefined}>
            <input value={imageDefaultBackground} onChange={(e) => setImageDefaultBackground(e.target.value)}
              placeholder={defaults?.imageDefaultBackground || 'auto'} className={inputCls} />
          </Field>
        </div>
      </section>

      {/* ── Generación de texto ────────────────────────────────────────────── */}
      <section className="space-y-4 pt-3 border-t border-ui-border">
        <h3 className="text-sm font-medium text-ui-text">Generación de texto</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Modelo de texto" help={defaults?.textModel ? `Si lo dejas vacío se usa: ${defaults.textModel}` : undefined}>
            <input value={textModel} onChange={(e) => setTextModel(e.target.value)}
              placeholder={defaults?.textModel || 'google/gemini-2.5-flash'} className={`${inputCls} font-mono`} />
          </Field>
          <Field label="Límite de textos por negocio al día" help={`Por defecto: ${defaults?.textDailyLimit ?? 50}`}>
            <input type="number" min={1} value={textDailyLimit} onChange={(e) => setTextDailyLimit(e.target.value)}
              placeholder={String(defaults?.textDailyLimit ?? 50)} className={inputCls} />
          </Field>
        </div>
      </section>

      {/* ── Estilo de imagen por industria ─────────────────────────────────── */}
      <section className="space-y-3 pt-3 border-t border-ui-border">
        <h3 className="text-sm font-medium text-ui-text">Estilo de imagen por industria</h3>
        <p className="text-sm text-ui-text-subtle">
          Orientan el “mood” de las imágenes según el giro del negocio. Lo que no definas aquí usa las pistas que ya trae el sistema.
        </p>
        <HintEditor rows={hints} onChange={setHints} defaults={defaults?.industryStyleHints ?? {}}
          industryPlaceholder="belleza" hintPlaceholder="cálido, luminoso, cercano…" />
      </section>

      {/* ── Tono de texto por industria ────────────────────────────────────── */}
      <section className="space-y-3 pt-3 border-t border-ui-border">
        <h3 className="text-sm font-medium text-ui-text">Tono de texto por industria</h3>
        <p className="text-sm text-ui-text-subtle">
          Guían cómo “suena” el texto que escribe la IA según el giro del negocio. Lo que no definas aquí usa el tono que ya trae el sistema.
        </p>
        <HintEditor rows={copyHints} onChange={setCopyHints} defaults={defaults?.industryCopyHints ?? {}}
          industryPlaceholder="belleza" hintPlaceholder="cálido y aspiracional, enfocado en bienestar…" />
      </section>

      {/* ── Prompts (avanzado) ─────────────────────────────────────────────── */}
      <section className="pt-3 border-t border-ui-border">
        <button type="button" onClick={() => setShowPrompts((v) => !v)}
          className="flex items-center justify-between w-full text-sm font-medium text-ui-text">
          <span>Instrucciones de la IA (avanzado)</span>
          <ChevronDown size={16} className={`transition-transform ${showPrompts ? 'rotate-180' : ''}`} />
        </button>
        <AnimatePresence initial={false}>
          {showPrompts && (
            <motion.div
              initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }} className="overflow-hidden">
              <div className="space-y-4 pt-4">
                <p className="text-sm text-ui-text-subtle">
                  Estas son las instrucciones con las que la IA escribe y crea hoy. Edítalas a tu gusto;
                  si cambias alguna, podrás volver a la original con “Restaurar”.
                </p>
                {defaults && (
                  <>
                    <PromptField label="Directiva de imagen (en inglés)"
                      value={pImageDirective} defaultValue={defaults.prompts.imageDirective} onChange={setPImageDirective} />
                    <PromptField label="Instrucción del redactor de contenido"
                      value={pCopywriter} defaultValue={defaults.prompts.copywriterSystem} onChange={setPCopywriter} />
                    <PromptField label="Instrucción del director de diseño (branding)"
                      value={pBranding} defaultValue={defaults.prompts.brandingDirectorSystem} onChange={setPBranding} />
                    <PromptField label="Instrucción para describir imágenes (texto alternativo)"
                      value={pAltText} defaultValue={defaults.prompts.altTextSystem} onChange={setPAltText} />
                    <PromptField label="Instrucción del traductor (español → inglés)"
                      value={pTranslator} defaultValue={defaults.prompts.translatorSystem} onChange={setPTranslator} />
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {error && <p className="text-sm text-ui-danger">{error}</p>}
      {saved && <p className="text-sm text-ui-accent">Cambios guardados.</p>}

      <button onClick={save} disabled={saving}
        className="px-4 py-2 bg-ui-accent text-ui-text-inverse text-sm rounded-md disabled:opacity-50 hover:opacity-90">
        {saving ? 'Guardando…' : 'Guardar cambios'}
      </button>
    </div>
  );
}
